import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import AddGeneralTopic from '../components/AddGeneralTopic';
import { addGeneralTopic, getGeneralTopics, getSubjects, updateGeneralTopic, deleteGeneralTopic } from '../lib/api';

const customStyles = `
  .table-image {
    width: 30px;
    height: 30px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #e5e7eb;
  }
  
  .content-preview {
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #6b7280;
    font-size: 0.875rem;
  }
`;

const GeneralTopicList = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [topics, setTopics] = useState([]);
  const [topicToEdit, setTopicToEdit] = useState(null);
  const [showEntries, setShowEntries] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('all');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  // Get token
  const getToken = () => {
    return localStorage.getItem('authToken') || localStorage.getItem('token');
  };

  // Fetch topics from API
  const fetchTopics = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await getGeneralTopics(token);
      const subjectsRes = await getSubjects(token);
      if (subjectsRes?.status === 'success') {
        const sortedSubjects = (subjectsRes.data || []).sort((a, b) => 
          a.SubjectName?.localeCompare(b.SubjectName)
        );
        setSubjects(sortedSubjects);
      } else if (subjectsRes?.data) {
        const sortedSubjects = subjectsRes.data.sort((a, b) => 
          a.SubjectName?.localeCompare(b.SubjectName)
        );
        setSubjects(sortedSubjects);
      }
      if (response.status === 'success') {
        setTopics(response.data || []);
      } else {
        console.error('Failed to fetch topics:', response.message);
        setTopics([]);
      }
    } catch (error) {
      console.error('Failed to fetch topics:', error);
      setTopics([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTopics();
  }, []);

  const toggleDrawer = (topic = null) => {
    setTopicToEdit(topic);
    setIsDrawerOpen(!isDrawerOpen);
  };

  // Handle form submission (now accepts videoFile as 4th argument)
  const handleSubmit = async (payload, isUpdate, pdfFiles, videoFile) => {
    try {
      const token = getToken();
      const formData = new FormData();

      // Append all text fields
      Object.keys(payload).forEach(key => {
        if (key !== 'ID' && typeof payload[key] !== 'object') {
          formData.append(key, payload[key]);
        }
      });

      // Append PDF files
      if (pdfFiles && typeof pdfFiles === 'object') {
        Object.entries(pdfFiles).forEach(([langCode, file]) => {
          if (file) {
            formData.append(`pdf_${langCode}`, file);
          }
        });
      }

      // Append Video file if new file was selected
      if (videoFile instanceof File) {
        formData.append('Video', videoFile);
      }

      if (isUpdate && payload.ID) {
        // Use update API with ID as URL parameter
        await updateGeneralTopic(token, formData, payload.ID);
      } else {
        // Use add API
        await addGeneralTopic(token, formData);
      }

      await fetchTopics();
      setIsDrawerOpen(false);
      setTopicToEdit(null);
    } catch (error) {
      console.error("Failed to save topic:", error);
      throw error;
    }
  };

  const handleEdit = (topic) => {
    toggleDrawer(topic);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this topic?')) {
      try {
        const token = getToken();
        await deleteGeneralTopic(token, id);
        await fetchTopics();
      } catch (error) {
        console.error("Failed to delete topic:", error);
      }
    }
  };

  // Filter and pagination
  const filteredTopics = topics.filter(topic => {
    const subjectMatch = selectedSubject === 'all' || 
                         topic.SubjectID?.toString() === selectedSubject;
    
    const searchMatch = !searchTerm || 
                       topic.TopicName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       subjects.find(s => s.ID === topic.SubjectID)?.SubjectName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return subjectMatch && searchMatch;
  }).sort((a, b) => {
    if (selectedSubject !== 'all') {
      return (b.Priority || 0) - (a.Priority || 0);
    }
    return 0;
  });
  
  const totalPages = Math.ceil(filteredTopics.length / showEntries);
  const startIndex = (currentPage - 1) * showEntries;
  const endIndex = startIndex + showEntries;
  const currentTopics = filteredTopics.slice(startIndex, endIndex);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, showEntries, selectedSubject]);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 3;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 2) {
        pages.push(1, 2, 3);
      } else if (currentPage >= totalPages - 1) {
        pages.push(totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(currentPage - 1, currentPage, currentPage + 1);
      }
    }

    return pages;
  };

  return (
    <>
      <style>{customStyles}</style>
      <div className="card p-0 radius-12">
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center flex-nowrap gap-2">
            <span className="text-sm fw-medium text-secondary-light mb-0">Show</span>
            <select
              className="form-select form-select-sm"
              value={showEntries}
              onChange={(e) => setShowEntries(parseInt(e.target.value))}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={-1}>All</option>
            </select>
            
            {/* Subject Dropdown Filter */}
            <div className="d-flex align-items-center gap-2">
              <select
                className="form-select form-select-sm"
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                style={{ minWidth: '250px' }}
              >
                <option value="all">Select Subject to filter the Topics</option>
                {subjects.map(subject => (
                  <option key={subject.ID} value={subject.ID}>
                    {subject.SubjectName}
                  </option>
                ))}
              </select>
            </div>

            <div className="navbar-search">
              <form
                className="navbar-search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              >
                <input type="text" className="bg-base h-40-px w-auto" name="search" placeholder="Search topics by name or subject" />
                <Icon icon="ion:search-outline" className="icon" />
              </form>
            </div>
          </div>
          <button
            onClick={() => toggleDrawer()}
            className="btn btn-yellow text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
            Add New Topic
          </button>
        </div>

        {loading ? (
          <div className="text-center py-4">Loading topics...</div>
        ) : (
          <>
            <div className="table-responsive p-16">
              <table className="table bordered-table sm-table mb-0">
                <thead>
                  <tr>
                    <th scope="col">Topic Name</th>
                    <th scope="col">Subject</th>
                    <th scope='col' className='text-center'>Priority</th>
                    <th scope="col" className="text-center">Status</th>
                    <th scope="col" className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {currentTopics.map((topic) => (
                    <tr key={topic.ID}>
                      <td>
                        <span className="text-md mb-0 fw-normal text-secondary-light">
                          {topic.TopicName}
                        </span>
                      </td>
                      <td>
                        <span>
                          {`${subjects.find(s => s.ID === topic.SubjectID)?.SubjectName || '-'}`}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span className="text-md mb-0 fw-normal text-center text-secondary-light">
                          {topic.Priority}
                        </span>
                      </td>
                      <td className="text-center">
                        <span
                          className={`px-24 py-4 radius-4 fw-medium text-sm ${topic.IsActive === 1 || topic.IsActive === true
                            ? 'bg-success-focus text-success-600 border border-success-main'
                            : 'bg-neutral-200 text-neutral-600 border border-neutral-400'
                            }`}
                        >
                          {topic.IsActive === 1 || topic.IsActive === true ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="text-center">
                        <div className="d-flex align-items-center gap-10 justify-content-center position-relative">
                          <button
                            type="button"
                            className="edit-btn bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            onClick={() => handleEdit(topic)}
                          >
                            <Icon icon="lucide:edit" className="menu-icon" />
                          </button>
                          {user?.IsDeleteAllowed && (
                          <button
                            type="button"
                            className="delete-btn bg-danger-focus text-danger-600 bg-hover-danger-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            onClick={() => handleDelete(topic.ID)}
                          >
                            <Icon icon="lucide:trash-2" className="menu-icon" />
                          </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredTopics.length === 0 && !loading && (
              <div className="text-center py-4 text-muted">
                No topics found
              </div>
            )}

            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 m-16">
              <span>
                Showing {filteredTopics.length === 0 ? 0 : startIndex + 1} to {Math.min(endIndex, filteredTopics.length)} of {filteredTopics.length} entries
              </span>
              {totalPages > 1 && (
                <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
                  <li className="page-item">
                    <button
                      className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                    >
                      <Icon icon="ep:d-arrow-left" />
                    </button>
                  </li>

                  {getPageNumbers().map(page => (
                    <li key={page} className="page-item">
                      <button
                        className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${currentPage === page ? 'bg-primary-600 text-white' : ''
                          }`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    </li>
                  ))}

                  <li className="page-item">
                    <button
                      className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                    >
                      <Icon icon="ep:d-arrow-right" />
                    </button>
                  </li>
                </ul>
              )}
            </div>
          </>
        )}
      </div>

      <AddGeneralTopic
        isOpen={isDrawerOpen}
        onClose={() => {
          setTopicToEdit(null);
          setIsDrawerOpen(false);
        }}
        onSubmit={handleSubmit}
        topicToEdit={topicToEdit}
      />
    </>
  );
};

export default GeneralTopicList;
